import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteDashboardsFromAPI,
  fetchDashboardsFromAPI,
  uploadDashboardFileToAPI,
  downloadDashboardFileFromAPI
} from "../../../services/DashboardService";

// Fetch Dashboards
export const fetchDashboards = createAsyncThunk(
  "dashboards/fetchDashboards",
  async ({ page, perPage, search, columnFilters, sort }, { rejectWithValue }) => {
    try {
      const response = await fetchDashboardsFromAPI(page, perPage, search, columnFilters, sort);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch dashboard data.");
    }
  }
);

// Delete Dashboard
export const deleteDashboard = createAsyncThunk(
  "dashboards/deleteDashboard",
  async (dashboardId, { rejectWithValue }) => {
    try {
      const response = await deleteDashboardsFromAPI(dashboardId);
      return dashboardId; // Return ID so we can remove it from the state
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete dashboard.");
    }
  }
);

// Upload Dashboard File
export const uploadDashboardFile = createAsyncThunk(
  "dashboards/uploadDashboardFile",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await uploadDashboardFileToAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to upload dashboard file.");
    }
  }
);

// Download Dashboard File
export const downloadDashboardFile = createAsyncThunk(
  "dashboards/downloadDashboardFile",
  async ({ fileType, vendorCode, period }, { rejectWithValue }) => {
    try {
      const response = await downloadDashboardFileFromAPI(fileType, vendorCode, period);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to download dashboard file.");
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboards",
  initialState: {
    dashboards: [],
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    firstItem: 1,
    dashboardSearch: "",
    loading: false,
    error: null,
    totalCount: 0,
    ngCount: 0,
    keywordSearch: "",
    // Per-column filters from the table header funnels, as { columnId: value }.
    columnFilters: {},
  },
  reducers: {
    setSearchDashboard: (state, action) => {
      state.dashboardSearch = action.payload;
    },
    setPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setVisibilityFilter: (state, action) => {
      state.currentFilter = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setKeywordSearch: (state, action) => {
      state.keywordSearch = action.payload;
    },
    setColumnFilters: (state, action) => {
      state.columnFilters = action.payload;
    },
    resetDashboardState: (state) => {
      state.dashboards = [];
      state.currentPage = 1;
      state.loading = false;
      state.error = null;
      state.columnFilters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dashboards
      .addCase(fetchDashboards.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboards.fulfilled, (state, action) => {
        const dashboards = action.payload;
        state.loading = false;
        state.dashboards = dashboards.data;
        state.currentPage = dashboards.current_page;
        state.lastPage = dashboards.last_page;
        state.perPage = dashboards.per_page;
        state.firstItem = dashboards.from;
        state.totalCount = dashboards.total;
        state.ngCount = dashboards.ng_count ?? 0;
      })
      .addCase(fetchDashboards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete Dashboard
      .addCase(deleteDashboard.fulfilled, (state, action) => {
        state.dashboards = state.dashboards.filter((dashboard) => dashboard.id !== action.payload);
      })
      .addCase(deleteDashboard.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Upload Dashboard File
      .addCase(uploadDashboardFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadDashboardFile.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Download Dashboard File
      .addCase(downloadDashboardFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadDashboardFile.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSearchDashboard,
  setPage,
  setVisibilityFilter,
  setPerPage,
  setKeywordSearch,
  setColumnFilters,
  resetDashboardState,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
